import { useLocation, useRoute } from 'wouter'
import { ChevronLeft, Loader2, Share2, Images } from 'lucide-react'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Button } from '@/components/ui/button'
import useSWR from 'swr'
import { apiFetch } from '@/utils'
import { useToast } from '@/hooks/use-toast'
import { getImageUrls } from '@/utils/imageUrls'
import GalleryGrid, { GalleryImage } from '@/components/GalleryGrid'

interface GalleryDetail {
  id: number | string
  heading: string
  page_title?: string
  page_description?: string
  content?: string
  filename?: string
  filename_alt?: string
  page_url?: string
  published_date?: string
  category_id?: number | null
  gallery?: GalleryImage[]
}

const LoadingState = () => {
  return (
    <div className="flex-1 flex flex-col items-center justify-center">
      <Loader2 className="h-8 w-8 text-[#EF4444] animate-spin" />
      <p className="text-[#BABABB] text-sm mt-4">Loading gallery...</p>
    </div>
  )
}

const ErrorState = ({ message }: { message?: string }) => {
  const [, navigate] = useLocation()

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6">
      <p className="text-[#EF4444] text-center mb-4">
        {message || 'Failed to load gallery. Please try again later.'}
      </p>
      <Button
        onClick={() => navigate('/galleries')}
        className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
      >
        Back to Galleries
      </Button>
    </div>
  )
}

export default function ShowPage() {
  const [, navigate] = useLocation()
  const [match, params] = useRoute('/galleries/:id')
  const { toast } = useToast()

  const galleryId = params?.id

  const { data, error, isLoading } = useSWR(
    galleryId ? `/v1/secrets_hideaway/galleries/${galleryId}` : null,
    (url: string) => apiFetch(url, { withCredentials: true }).then((res) => res.json())
  )

  const gallery: GalleryDetail | null = data?.success && data?.details?.items?.[0] ? data.details.items[0] : null
  const imageUrls = gallery?.filename ? getImageUrls('galleries', gallery.filename) : null

  const formattedDate = gallery?.published_date
    ? new Date(gallery.published_date).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null

  const handleShare = async () => {
    const url = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({
          title: gallery?.heading || 'Gallery',
          url
        })
      } catch (err) {
        // User cancelled or share failed
      }
    } else {
      await navigator.clipboard.writeText(url)
      toast({
        title: 'Link copied',
        description: 'Gallery link copied to clipboard'
      })
    }
  }

  return (
    <ErrorBoundary>
      <div className="min-h-auto bg-[#18181B] flex flex-col h-full">
        <div className="flex-1 max-w-5xl mx-auto w-full px-5 pb-8">
          {/* Header with back button */}
          <div
            role="banner"
            className="sticky top-0 z-50 bg-[#18181B] flex items-center justify-between text-white py-3 mb-[12px]"
          >
            <div className="flex items-center justify-start gap-2">
              <button
                type="button"
                aria-label="Go back"
                onClick={() => navigate('/galleries')}
                className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
              >
                <ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
              </button>

              <h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
                Gallery
              </h1>
            </div>

            {gallery && (
              <button
                type="button"
                aria-label="Share gallery"
                onClick={handleShare}
                className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
              >
                <Share2 className="h-[18px] w-[18px] text-[#ffffff]" />
              </button>
            )}
          </div>

          {/* Content */}
          {isLoading ? (
            <LoadingState />
          ) : error || !gallery ? (
            <ErrorState />
          ) : (
            <>
            {/* Gallery Header */}
            <div className="mb-6">
              {/* Title */}
              <h2 className="text-[#ffffff] font-bold text-2xl mb-2">
                {gallery.heading}
              </h2>

              {/* Meta */}
              {formattedDate && (
                <p className="text-[#BABABB] text-sm">
                  {formattedDate}
                </p>
              )}

              {/* Description */}
              {gallery.page_description && (
                <p className="text-[#BABABB] mt-4 leading-relaxed">
                  {gallery.page_description}
                </p>
              )}
            </div>

            {/* Gallery Grid or Single Image */}
            {gallery.gallery && gallery.gallery.length > 0 ? (
              <GalleryGrid images={gallery.gallery} />
            ) : imageUrls ? (
              <div className="w-full overflow-hidden rounded-xl">
                <img
                  src={imageUrls.source}
                  alt={gallery.filename_alt || gallery.heading}
                  className="w-full h-auto object-contain"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <Images className="h-16 w-16 text-[#4C4C4C] mb-4" />
                <p className="text-[#BABABB]">No images available</p>
              </div>
            )}

            {/* Full Content */}
            {gallery.content && (
              <div className="prose prose-invert max-w-none mt-6">
                <div
                  className="text-[#BABABB] leading-relaxed [&>p]:mb-4"
                  dangerouslySetInnerHTML={{ __html: gallery.content }}
                />
              </div>
            )}
            </>
          )}
        </div>
      </div>
    </ErrorBoundary>
  )
}
